/**
 * Authentication Context
 * FEA-125: User Authentication & Permissions
 * SSO: Redirects to AUTH login on 401
 *
 * Provides current user info and permission helpers throughout the app.
 * Fetches user from /api/auth/me on mount.
 */

import React, { createContext, useContext, useCallback, ReactNode } from 'react'
import { logger } from '@/utils/logger';
import { useQuery } from '@tanstack/react-query'
import { AUTH_URL } from '@/api/config'
import type { Capability, UserRole } from '@fmeastudio/shared'
import { can as canCapability } from '@fmeastudio/shared'

/**
 * User role types matching backend UserRole enum. packages/shared/src/constants/authz.ts
 * is the single source of truth for the role -> capability matrix; re-exported here so
 * existing call sites that `import type { UserRole } from '@/contexts/AuthContext'`
 * keep working unchanged.
 */
export type { UserRole }

/**
 * Authenticated user from /api/auth/me
 */
export interface AuthUser {
  id: string
  email: string
  name: string
  role: UserRole
  isLocalAdmin: boolean
  iat?: number
  exp?: number
  ip?: string
  permissions: {
    canEdit: boolean
    canDelete: boolean
    canExport: boolean
    canManageUsers: boolean
    canAccessAIChat: boolean
  }
}

/**
 * Auth context value
 */
export interface AuthContextType {
  /** Current authenticated user (null if loading or error) */
  user: AuthUser | null
  /** True while fetching user */
  isLoading: boolean
  /** Error if fetch failed */
  error: Error | null
  /** Refetch user (e.g., after role change) */
  refetch: () => void
  /** Log out and redirect to Tracer login */
  logout: () => Promise<void>

  // Convenience permission checks
  /** True if user is ADMIN or local admin */
  isAdmin: boolean
  /** True if accessing via local network (god mode) */
  isLocalAdmin: boolean
  /** True if can create/edit entities (ADMIN, ENGINEER, or local admin) */
  canEdit: boolean
  /** True if can delete entities (ADMIN or local admin) */
  canDelete: boolean
  /** True if can export data (not GUEST) */
  canExport: boolean
  /** True if can manage users (ADMIN or local admin) */
  canManageUsers: boolean
  /** True if can access AI chat (ADMIN or local admin) */
  canAccessAIChat: boolean
  /**
   * Capability check against the shared authz matrix (packages/shared/src/
   * constants/authz.ts). Prefer this over the legacy canEdit/canDelete/etc
   * booleans for anything mapping onto the 8 Interlock capabilities
   * (ENGINEERING_WRITE, TRADE_PARTICIPATE, ARBITRATE, GATE_DECLARE,
   * CAMPAIGN_RUN, CAMPAIGN_SCORE, ADMIN_OVERRIDE) — it's a single source of
   * truth shared with the backend's requireCapability() middleware, so a CTA
   * gated by `hasCapability('GATE_DECLARE')` will never drift from what the
   * server actually enforces. NOTE: purely a UX nicety (defense-in-depth) —
   * the server is the real boundary, this only hides/disables buttons the
   * user isn't allowed to press.
   */
  hasCapability: (capability: Capability) => boolean
}

const AuthContext = createContext<AuthContextType | null>(null)

/**
 * RS3 (round 10 resilience) — exported so other data layers (interlockApi.ts) can
 * reuse the SAME "you're logged out" surfacing this context uses on `/api/auth/me`
 * 401, instead of a fetch layer inventing its own (weaker) auth-expiry story or —
 * worse — silently falling back to fixture data as if a 401 were just another
 * network hiccup.
 */
export function redirectToLogin() {
  const callbackUrl = `${window.location.origin}/api/auth/callback`
  window.location.href = `${AUTH_URL}/login?redirect=${encodeURIComponent(callbackUrl)}`
}

/**
 * Fetch current user from auth API
 */
async function fetchCurrentUser(): Promise<AuthUser | null> {
  try {
    const res = await fetch('/api/auth/me', { credentials: 'include' })
    if (!res.ok) {
      if (res.status === 401) {
        // Not authenticated — redirect to AUTH login
        redirectToLogin()
        return null
      }
      throw new Error(`Auth error: ${res.status}`)
    }
    const json = await res.json()
    return json.data as AuthUser
  } catch (err) {
    logger.error('Failed to fetch current user', err, { module: 'AuthContext' });
    throw err
  }
}

/**
 * Auth provider component
 * Wrap your app with this to provide auth context
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const {
    data: user,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['auth', 'me'],
    queryFn: fetchCurrentUser,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 1, // Only retry once on failure
  })

  // Logout: clear FMEA cookie and redirect to AUTH login (without callback
  // so the user doesn't auto-bounce back via SSO)
  const logout = useCallback(async () => {
    try {
      await fetch('/api/auth/logout', { method: 'POST', credentials: 'include' })
    } catch {
      // Best-effort
    }
    window.location.href = `${AUTH_URL}/login`
  }, [])

  // Compute permission booleans
  const isLocalAdmin = user?.isLocalAdmin ?? false
  const isAdmin = isLocalAdmin || user?.role === 'ADMIN'
  const canEdit = user?.permissions?.canEdit ?? isLocalAdmin
  const canDelete = user?.permissions?.canDelete ?? isLocalAdmin
  const canExport = user?.permissions?.canExport ?? isLocalAdmin
  const canManageUsers = user?.permissions?.canManageUsers ?? isLocalAdmin
  const canAccessAIChat = user?.permissions?.canAccessAIChat ?? isLocalAdmin
  const hasCapability = useCallback(
    (capability: Capability) => canCapability(isLocalAdmin ? 'ADMIN' : user?.role, capability),
    [isLocalAdmin, user?.role]
  )

  const value: AuthContextType = {
    user: user ?? null,
    isLoading,
    error: error as Error | null,
    refetch,
    logout,
    isAdmin,
    isLocalAdmin,
    canEdit,
    canDelete,
    canExport,
    canManageUsers,
    canAccessAIChat,
    hasCapability,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

/**
 * Hook to access auth context
 * Must be used within AuthProvider
 */
export function useAuth(): AuthContextType {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider')
  }
  return context
}

/**
 * Hook to require specific roles
 * Returns true if user has one of the allowed roles
 */
export function useRequireRole(...allowedRoles: UserRole[]): boolean {
  const { user, isLocalAdmin } = useAuth()

  // Local admin always has access
  if (isLocalAdmin) return true

  // Check if user has one of the allowed roles
  if (!user) return false
  return allowedRoles.includes(user.role)
}

export default AuthContext
